import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';

/** A breadcrumb hop under the console base. Omit `slug` to mark the current page. */
export interface CdnCrumb {
  label: ReactNode;
  /** Path segment under `/services/cdn/:serviceId`; omit for the current (non-link) crumb. */
  slug?: string;
}

const ROOT_LABEL = 'Multi-CDN Orchestration';

/**
 * Consistent breadcrumb trail for every service-scoped CDN console page, shown
 * in the PageHeader's small text above the title. Always rooted at the service
 * overview ("Multi-CDN Orchestration"); pass the sub-page hops via `trail`. The
 * overview itself passes no trail (root renders as the current page).
 */
export function CdnConsoleBreadcrumbs({ trail = [] }: { trail?: CdnCrumb[] }) {
  const { serviceId } = useParams<{ serviceId: string }>();
  // Org-level pages (no :serviceId, e.g. /services/cdn/usage) root at the CDN home.
  const base = serviceId ? `/services/cdn/${serviceId}` : '/services/cdn';

  // Build a flat array of items so an empty trail produces a single crumb with
  // no trailing separator (Breadcrumbs adds a chevron before every item after
  // the first, including an empty `trail.map([])` child if passed inline).
  const items = [
    trail.length === 0 ? (
      <BreadcrumbItem key="root" current>{ROOT_LABEL}</BreadcrumbItem>
    ) : (
      <BreadcrumbItem key="root" asChild>
        <Link to={base}>{ROOT_LABEL}</Link>
      </BreadcrumbItem>
    ),
    ...trail.map((crumb, i) =>
      crumb.slug ? (
        <BreadcrumbItem key={i} asChild>
          <Link to={`${base}/${crumb.slug}`}>{crumb.label}</Link>
        </BreadcrumbItem>
      ) : (
        <BreadcrumbItem key={i} current>
          {crumb.label}
        </BreadcrumbItem>
      ),
    ),
  ];

  return <Breadcrumbs>{items}</Breadcrumbs>;
}
